import React, { Component } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';

/**
 * Error reporting hook - placeholder for Sentry/other services
 * Replace this implementation when adding real error tracking
 */
const reportError = (error, errorInfo, componentStack) => {
  // In development, log detailed error info
  if (process.env.NODE_ENV === 'development') {
    console.group('🔴 ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', componentStack);
    console.groupEnd();
  } else {
    // In production, log minimal info
    // TODO: Replace with Sentry.captureException(error) when Sentry is configured
    console.error('Application error:', error?.message || 'Unknown error');
  }
  
  // Placeholder for future Sentry integration:
  // if (typeof Sentry !== 'undefined') {
  //   Sentry.captureException(error, {
  //     extra: { componentStack, errorInfo }
  //   });
  // }
};

/**
 * Friendly error fallback UI component
 */
const ErrorFallback = ({ error, resetError, level = 'page' }) => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const isRootLevel = level === 'root';

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6 bg-white" data-testid="error-boundary-fallback">
      <div className="max-w-md w-full text-center">
        {/* Error Icon */}
        <div className="mb-6">
          <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
        </div>

        {/* Error Message */}
        <h2 className="font-sweet-sans text-2xl font-semibold text-earth-brown mb-3">
          Oeps, er ging iets mis
        </h2>
        
        <p className="font-sweet-sans text-gray-600 mb-6">
          {isRootLevel 
            ? 'Er is een onverwachte fout opgetreden. Probeer de pagina opnieuw te laden.'
            : 'Dit onderdeel kon niet worden geladen. Probeer het opnieuw of ga terug naar de vorige pagina.'
          }
        </p>

        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <p className="font-mono text-xs text-red-700 break-all">
              <strong>Dev Error:</strong> {error.message || 'Unknown error'}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {resetError && !isRootLevel && (
            <Button
              onClick={resetError}
              variant="outline"
              className="border-earth-brown text-earth-brown hover:bg-earth-brown hover:text-white"
              data-testid="error-retry-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Opnieuw proberen
            </Button>
          )}
          
          {!isRootLevel && (
            <Button
              onClick={handleGoBack}
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="error-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ga terug
            </Button>
          )}

          <Button
            onClick={handleReload}
            className="bg-terra-beige hover:bg-terra-beige/90 text-white"
            data-testid="error-reload-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Pagina herladen
          </Button>

          {isRootLevel && (
            <Button
              onClick={handleGoHome}
              className="bg-earth-brown hover:bg-earth-brown/90 text-white"
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Naar Homepage
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Error Boundary class component
 * Catches JavaScript errors in child component tree
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    this.setState({ errorInfo });
    reportError(error, errorInfo, errorInfo?.componentStack);
  }

  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorFallback 
          error={this.state.error}
          resetError={this.resetError}
          level={this.props.level || 'page'}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap pages with error boundary
 */
export const withErrorBoundary = (WrappedComponent, options = {}) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary level={options.level || 'page'} fallback={options.fallback}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  
  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  
  return WithErrorBoundary;
};

/**
 * Root-level error boundary with full page layout
 */
export const RootErrorBoundary = ({ children }) => (
  <ErrorBoundary level="root">
    {children}
  </ErrorBoundary>
);

/**
 * Page-level error boundary
 */
export const PageErrorBoundary = ({ children, fallback }) => (
  <ErrorBoundary level="page" fallback={fallback}>
    {children}
  </ErrorBoundary>
);

export { ErrorBoundary, ErrorFallback };
export default ErrorBoundary;
